import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import type { AppsSubTab } from "@/lib/bundle-state";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ChevronDown, TriangleAlert } from "lucide-react";
import { containerPermissions } from "@/lib/boo-permissions";
import { AppSignIn } from "./integrations/AppSignIn";
import { AppSourceDefault } from "./integrations/AppSourceDefault";
import { BuiltInCapabilities, Bundles, DeployPlatforms } from "./integrations/Bundles";
import { SignInChanges } from "./integrations/SignInChanges";

// …/containers/$containerId/apps. App store: the apps (bundles) on this Boo and
// the ones it can add; `?app=` opens one's details. App configuration: who can
// open the apps, where Boo saves their code, and, under Advanced, where they
// run and the capabilities built into every Boo. Bundles fetches its own data
// + permissions from the bundle routes.
export function AppsTab() {
  const params = useParams({ strict: false }) as { orgId?: string; containerId?: string };
  const containerId = params.containerId!;
  const { tab, app } = useSearch({ strict: false }) as { tab?: AppsSubTab; app?: string };
  const navigate = useNavigate();

  const { data: listResponse } = useQuery({
    queryKey: ["containers"],
    queryFn: api.containers.list,
  });
  const container = listResponse?.data.find((c) => c.id === containerId);
  const permissions = containerPermissions(container, listResponse?._permissions);

  if (!container) return null;

  const orgId = params.orgId ?? container.organizationId;
  const active: AppsSubTab = tab ?? "store";
  const go = (search: { tab: AppsSubTab; app?: string }) =>
    navigate({ to: "/orgs/$orgId/containers/$containerId/apps", params: { orgId, containerId }, search, replace: true });

  return (
    <div className="flex flex-1 flex-col min-h-0 overflow-auto p-6">
      <Tabs value={active} onValueChange={(t) => go({ tab: t as AppsSubTab })} className="max-w-5xl gap-6">
        <TabsList>
          <TabsTrigger value="store">App store</TabsTrigger>
          <TabsTrigger value="configuration">App configuration</TabsTrigger>
        </TabsList>

        {/* The catalog uses the width. */}
        <TabsContent value="store">
          <Bundles
            containerId={container.id}
            details={app ?? null}
            onDetailsChange={(id) => go({ tab: "store", ...(id ? { app: id } : {}) })}
          />
        </TabsContent>

        {/* Settings keep a reading width. A custom domain for apps goes here too. */}
        <TabsContent value="configuration" className="max-w-2xl flex flex-col gap-10">
          <AppSignIn containerId={container.id} />
          <SignInChanges containerId={container.id} />
          <AppSourceDefault container={container} canEdit={permissions?.canEdit === true} />
          <Collapsible>
            <CollapsibleTrigger className="group flex items-center gap-1.5 text-base font-semibold">
              <ChevronDown className="size-4 text-muted-foreground transition-transform group-data-[state=closed]:-rotate-90" />
              Advanced
            </CollapsibleTrigger>
            <CollapsibleContent className="flex flex-col gap-8 pt-4">
              <p className="flex items-center gap-2 text-sm text-amber-800 dark:text-amber-300">
                <TriangleAlert className="size-4 shrink-0" />
                Changes here affect everyone on this Boo.
              </p>
              <DeployPlatforms containerId={container.id} />
              <BuiltInCapabilities containerId={container.id} orgId={orgId} />
            </CollapsibleContent>
          </Collapsible>
        </TabsContent>
      </Tabs>
    </div>
  );
}
